import { BoundDateField, BoundNumberField, Button, Css, IconButton, TextField, useComputed } from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router";
import { PdfViewer } from "src/components/PdfViewer";
import {
  AssetInfoFragment,
  InputMaybe,
  LotAcqLotAcqPurchaseAgreementFragment,
  LotAcqLotFragment,
  SaveLotAcqPurchaseAgreementInput,
  useDeleteLotAcqPurchaseAgreementMutation,
  useLotAcqPurchaseAgreementDocumentsQuery,
  useSaveLotAcqPurchaseAgreementMutation,
  useSendLotAcqPurchaseAgreementDepositCancelationForBookingMutation,
  useSendLotAcqPurchaseAgreementDepositForBookingMutation,
} from "src/generated/graphql-types";
import { PageHeader } from "src/routes/layout/PageHeader";
import { DocumentIdParams } from "src/routes/routesDef";
import { createLotDetailsUrl } from "src/RouteUrls";
import { DateOnly, ensureDateOnly } from "src/utils/dates";
import { queryResult } from "src/utils/queryResult";

export function LotAcqPurchaseAgreementEditor() {
  const { documentId } = useParams<DocumentIdParams>();

  const documentsQuery = useLotAcqPurchaseAgreementDocumentsQuery({
    variables: { documentIds: [documentId] },
    skip: !documentId,
  });

  return queryResult(documentsQuery, (data) => {
    const document = data.documents[0];
    const lot = document.parent as LotAcqLotFragment;
    const documentId = document.id;
    const lapa = document.lotAcqPurchaseAgreement;
    return (
      <LotAcqPurchaseAgreementForm
        documentId={documentId}
        lot={lot}
        assetInfo={document.asset}
        lotAcqPurchaseAgreement={lapa!}
      />
    );
  });
}

type LotAcqPurchaseAgreementEditorProps = {
  documentId: string;
  lot: LotAcqLotFragment;
  assetInfo: AssetInfoFragment;
  lotAcqPurchaseAgreement: LotAcqLotAcqPurchaseAgreementFragment;
};

function LotAcqPurchaseAgreementForm({
  documentId,
  lot,
  assetInfo,
  lotAcqPurchaseAgreement,
}: LotAcqPurchaseAgreementEditorProps) {
  const [saveLotAcqPurchaseAgreement] = useSaveLotAcqPurchaseAgreementMutation();
  const [deleteLotAcqPurchaseAgreement] = useDeleteLotAcqPurchaseAgreementMutation();
  const [sendDepositForBooking] = useSendLotAcqPurchaseAgreementDepositForBookingMutation();
  const [sendDepositCancelationForBooking] = useSendLotAcqPurchaseAgreementDepositCancelationForBookingMutation();
  const history = useHistory();

  // Once the deposit has been booked the agreement is locked for editing - the only
  // remaining action is to book the cancellation against the original deposit JE.
  const isDepositBooked = lotAcqPurchaseAgreement?.bookingDepositEventSent;

  const formState = useFormState({
    config: formConfig,
    init: {
      input: mapPurchaseAgreementToForm(lotAcqPurchaseAgreement),
    },
  });

  const isDirty = useComputed(() => formState.dirty, [formState]);

  const saveLotAcqPurchaseAgreementFunction = useCallback(
    async (formState: ObjectState<LotAcqPurchaseAgreementFormInput>) => {
      const result = await saveLotAcqPurchaseAgreement({
        variables: { input: mapToInput(formState.value) },
      });

      // Re-sync the form with what the backend persisted
      const saved = result.data?.saveLotAcqPurchaseAgreement?.lotAcqPurchaseAgreement;
      if (saved) {
        formState.set(mapPurchaseAgreementToForm(saved));
      }
      formState.commitChanges();
    },
    [saveLotAcqPurchaseAgreement],
  );

  function mapToInput(formStateValue: LotAcqPurchaseAgreementFormInput): SaveLotAcqPurchaseAgreementInput {
    const { documentId, lotId, depositPaidDate, depositCancelledDate, ...others } = formStateValue;
    return {
      ...others,
      documentId,
      lotId,
      depositPaidDate: depositPaidDate ? new DateOnly(depositPaidDate) : undefined,
      depositCancelledDate: depositCancelledDate ? new DateOnly(depositCancelledDate) : undefined,
    };
  }

  const title = "Edit Purchase Agreement for " + lot?.address?.street1 + " (" + lot?.id + ")";

  const depositBookingTooltip = lotAcqPurchaseAgreement?.canSendDepositForBooking.allowed
    ? "Books the Land Deposit to Accounting - cannot be undone"
    : lotAcqPurchaseAgreement?.canSendDepositForBooking.disabledReasons.map((reason) => reason.message).join(", ");

  const cancelationBookingTooltip = lotAcqPurchaseAgreement?.canSendDepositCancelationForBooking.allowed
    ? "Books the Land Deposit Cancellation to Accounting - cannot be undone"
    : lotAcqPurchaseAgreement?.canSendDepositCancelationForBooking.disabledReasons
        .map((reason) => reason.message)
        .join(", ");

  return (
    <>
      <PageHeader title={title} backButton={createLotDetailsUrl(lot.id)} />
      <TextField
        label="Investment Status"
        value={lotAcqPurchaseAgreement?.lot.lotInvestmentStatus.name}
        readOnly
        onChange={() => {}}
      />
      <BoundNumberField
        field={formState.purchasePriceInCents}
        label="Purchase Price (from this document)"
        type="cents"
        readOnly={isDepositBooked}
      />
      <BoundNumberField
        field={formState.earnestMoneyInCents}
        label="Earnest Money"
        type="cents"
        readOnly={isDepositBooked}
      />
      <BoundNumberField field={formState.optionFeeInCents} label="Option Fee" type="cents" readOnly={isDepositBooked} />
      <BoundDateField field={formState.depositPaidDate} label="GL Deposit Date" readOnly={isDepositBooked} />
      <BoundDateField field={formState.depositCancelledDate} label="GL Cancel Date" disabled={!isDepositBooked} />
      <div css={Css.df.fdr.mt1.gap1.$}>
        <Button
          variant="primary"
          label="Save"
          disabled={!isDirty}
          onClick={async () => {
            await saveLotAcqPurchaseAgreementFunction(formState);
            formState.commitChanges();
          }}
        />
        {!isDepositBooked && (
          <>
            <Button
              variant="secondary"
              label="Book Deposit to Accounting"
              icon="finances"
              onClick={async () => {
                await sendDepositForBooking({ variables: { input: { id: lotAcqPurchaseAgreement.id } } });
                history.push({ pathname: createLotDetailsUrl(lot.id) });
              }}
              disabled={isDirty || !lotAcqPurchaseAgreement?.canSendDepositForBooking.allowed}
              tooltip={isDirty ? "Save your changes before booking" : depositBookingTooltip}
            />
            <IconButton
              icon="trash"
              tooltip="Delete Lot Acq Purchase Agreement"
              onClick={async () => {
                await deleteLotAcqPurchaseAgreement({
                  variables: { input: { id: lotAcqPurchaseAgreement.id } },
                });
                history.push({ pathname: createLotDetailsUrl(lot.id) });
              }}
            />
          </>
        )}
        {isDepositBooked && (
          <Button
            variant="secondary"
            label="Book Cancelation to Accounting"
            icon="finances"
            onClick={async () => {
              await sendDepositCancelationForBooking({ variables: { input: { id: lotAcqPurchaseAgreement.id } } });
              history.push({ pathname: createLotDetailsUrl(lot.id) });
            }}
            disabled={isDirty || !lotAcqPurchaseAgreement?.canSendDepositCancelationForBooking.allowed}
            tooltip={isDirty ? "Save your changes before booking" : cancelationBookingTooltip}
          />
        )}
      </div>
      <PdfViewer
        hasHeader
        title={"Uploaded Purchase Agreement PDF"}
        assets={[assetInfo]}
        pdfPageWidth={getDynamicPdfVierwerWidth()}
      />
    </>
  );
}

export type LotAcqPurchaseAgreementFormInput = {
  id: InputMaybe<string>;
  lotId: InputMaybe<string>;
  documentId: InputMaybe<string>;
  purchasePriceInCents: InputMaybe<number>;
  earnestMoneyInCents: InputMaybe<number>;
  optionFeeInCents: InputMaybe<number>;
  depositPaidDate: InputMaybe<Date>;
  depositCancelledDate: InputMaybe<Date>;
};

export const formConfig: ObjectConfig<LotAcqPurchaseAgreementFormInput> = {
  id: { type: "value" },
  lotId: { type: "value", rules: [required] },
  documentId: { type: "value", rules: [required] },
  purchasePriceInCents: { type: "value", rules: [required] },
  earnestMoneyInCents: { type: "value", rules: [required] },
  optionFeeInCents: { type: "value", rules: [required] },
  // Dates are only relevant at booking time, so they are not required to save.
  depositPaidDate: { type: "value" },
  depositCancelledDate: { type: "value" },
};

function mapPurchaseAgreementToForm(
  purchaseAgreement: LotAcqLotAcqPurchaseAgreementFragment,
): LotAcqPurchaseAgreementFormInput {
  return {
    id: purchaseAgreement.id,
    lotId: purchaseAgreement.lot.id,
    documentId: purchaseAgreement.document.id,
    purchasePriceInCents: purchaseAgreement.purchasePriceInCents,
    earnestMoneyInCents: purchaseAgreement.earnestMoneyInCents,
    optionFeeInCents: purchaseAgreement.optionFeeInCents,
    depositPaidDate: purchaseAgreement.depositPaidDate ? ensureDateOnly(purchaseAgreement.depositPaidDate) : undefined,
    depositCancelledDate: purchaseAgreement.depositCancelledDate
      ? ensureDateOnly(purchaseAgreement.depositCancelledDate)
      : undefined,
  };
}

export function getDynamicPdfVierwerWidth(browserWidth?: number) {
  const { innerHeight } = window;
  return innerHeight > 900 ? 820 : innerHeight > 800 ? 760 : 640;
}
